/**
 * AnalyticsLoggerサービス
 *
 * FrontendLoggerを使用してマーケティング分析イベントとUI操作イベントを記録する。
 *
 * Requirements:
 * - 6.1: ログイン時に auth.login イベントを記録
 * - 6.2: ログアウト時に auth.logout イベントを記録
 * - 6.3: 新規登録時に auth.register イベントを記録
 * - 6.4: 視聴開始時に stream.view_start イベントを記録
 * - 6.5: 視聴終了時に stream.view_end イベントを記録
 * - 6.6: ポイント購入時に payment.purchase イベントを記録
 * - 6.7: ポイント消費時に payment.consume イベントを記録
 * - 6.8: eKYC開始時に ekyc.start イベントを記録
 * - 6.9: eKYC完了時に ekyc.complete イベントを記録
 * - 6.10: ページ遷移時に navigation.page_view イベントを記録
 * - 7.1: ボタンクリック時に ui.button_click イベントを記録
 * - 7.2: リンククリック時に ui.link_click イベントを記録
 * - 7.3: モーダル開閉時に ui.modal_open/ui.modal_close イベントを記録
 * - 7.4: 通知一覧表示時に notification.list_view イベントを記録
 * - 7.5: 通知クリック時に notification.click イベントを記録
 * - 7.6: フォーム送信時に ui.form_submit イベントを記録
 * - 7.7: チャット送信時に chat.message_send イベントを記録
 * - 7.8: 動画プレイヤー操作時に player.action イベントを記録
 * - 7.9: タブ切り替え時に ui.tab_change イベントを記録
 */

import { FrontendLogger } from './logger';

export type PlayerAction = 'play' | 'pause' | 'seek' | 'fullscreen' | 'mute' | 'unmute';
export type EkycResult = 'approved' | 'rejected' | 'pending' | 'error';

export class AnalyticsLogger {
  private static instance: AnalyticsLogger | null = null;
  private logger: FrontendLogger;

  private constructor() {
    this.logger = FrontendLogger.getInstance();
  }

  /**
   * シングルトンインスタンスを取得する
   */
  public static getInstance(): AnalyticsLogger {
    if (!AnalyticsLogger.instance) {
      AnalyticsLogger.instance = new AnalyticsLogger();
    }
    return AnalyticsLogger.instance;
  }

  /**
   * シングルトンインスタンスをリセットする（テスト用）
   */
  public static resetInstance(): void {
    AnalyticsLogger.instance = null;
  }

  // ========================================
  // 認証イベント (Requirements 6.1, 6.2, 6.3)
  // ========================================

  /**
   * ログインイベントを記録する
   */
  public trackLogin(userId: number, loginMethod: string): void {
    this.logger.info('User logged in', {
      eventType: 'auth.login',
      eventData: {
        user_id: userId,
        login_method: loginMethod,
      },
    });
  }

  /**
   * ログアウトイベントを記録する
   */
  public trackLogout(): void {
    this.logger.info('User logged out', {
      eventType: 'auth.logout',
    });
  }

  /**
   * 新規登録イベントを記録する
   */
  public trackRegister(userId: number): void {
    this.logger.info('User registered', {
      eventType: 'auth.register',
      eventData: {
        user_id: userId,
      },
    });
  }

  // ========================================
  // 視聴イベント (Requirements 6.4, 6.5)
  // ========================================

  /**
   * 視聴開始イベントを記録する
   */
  public trackStreamViewStart(streamId: string, streamerId: string): void {
    this.logger.info('Stream view started', {
      eventType: 'stream.view_start',
      eventData: {
        stream_id: streamId,
        streamer_id: streamerId,
      },
    });
  }

  /**
   * 視聴終了イベントを記録する
   */
  public trackStreamViewEnd(
    streamId: string,
    streamerId: string,
    watchDurationSeconds: number
  ): void {
    this.logger.info('Stream view ended', {
      eventType: 'stream.view_end',
      eventData: {
        stream_id: streamId,
        streamer_id: streamerId,
        watch_duration_seconds: watchDurationSeconds,
      },
    });
  }

  // ========================================
  // 課金イベント (Requirements 6.6, 6.7)
  // ========================================

  /**
   * ポイント購入イベントを記録する
   */
  public trackPurchase(amount: number, points: number): void {
    this.logger.info('Points purchased', {
      eventType: 'payment.purchase',
      eventData: {
        amount,
        points,
      },
    });
  }

  /**
   * ポイント消費イベントを記録する
   */
  public trackConsume(target: string, points: number): void {
    this.logger.info('Points consumed', {
      eventType: 'payment.consume',
      eventData: {
        target,
        points,
      },
    });
  }

  // ========================================
  // eKYCイベント (Requirements 6.8, 6.9)
  // ========================================

  /**
   * eKYC開始イベントを記録する
   */
  public trackEkycStart(): void {
    this.logger.info('eKYC started', {
      eventType: 'ekyc.start',
    });
  }

  /**
   * eKYC完了イベントを記録する
   */
  public trackEkycComplete(result: EkycResult): void {
    this.logger.info('eKYC completed', {
      eventType: 'ekyc.complete',
      eventData: {
        result,
      },
    });
  }

  // ========================================
  // ナビゲーションイベント (Requirement 6.10)
  // ========================================

  /**
   * ページ遷移イベントを記録する
   */
  public trackPageView(fromPath: string, toPath: string): void {
    this.logger.info('Page viewed', {
      eventType: 'navigation.page_view',
      eventData: {
        from_path: fromPath,
        to_path: toPath,
      },
    });
  }

  // ========================================
  // UI操作イベント (Requirements 7.1-7.10)
  // ========================================

  /**
   * ボタンクリックイベントを記録する
   */
  public trackButtonClick(buttonId: string, buttonLabel: string, page: string): void {
    this.logger.info('Button clicked', {
      eventType: 'ui.button_click',
      eventData: {
        button_id: buttonId,
        button_label: buttonLabel,
        page,
      },
    });
  }

  /**
   * リンククリックイベントを記録する
   */
  public trackLinkClick(href: string, linkText: string, page: string): void {
    this.logger.info('Link clicked', {
      eventType: 'ui.link_click',
      eventData: {
        href,
        link_text: linkText,
        page,
      },
    });
  }

  /**
   * モーダル開くイベントを記録する
   */
  public trackModalOpen(modalId: string): void {
    this.logger.info('Modal opened', {
      eventType: 'ui.modal_open',
      eventData: {
        modal_id: modalId,
      },
    });
  }

  /**
   * モーダル閉じるイベントを記録する
   */
  public trackModalClose(modalId: string): void {
    this.logger.info('Modal closed', {
      eventType: 'ui.modal_close',
      eventData: {
        modal_id: modalId,
      },
    });
  }

  /**
   * 通知一覧表示イベントを記録する
   */
  public trackNotificationListView(unreadCount: number): void {
    this.logger.info('Notification list viewed', {
      eventType: 'notification.list_view',
      eventData: {
        unread_count: unreadCount,
      },
    });
  }

  /**
   * 通知クリックイベントを記録する
   */
  public trackNotificationClick(notificationId: string, notificationType: string): void {
    this.logger.info('Notification clicked', {
      eventType: 'notification.click',
      eventData: {
        notification_id: notificationId,
        notification_type: notificationType,
      },
    });
  }

  /**
   * フォーム送信イベントを記録する
   */
  public trackFormSubmit(formId: string, success: boolean): void {
    this.logger.info('Form submitted', {
      eventType: 'ui.form_submit',
      eventData: {
        form_id: formId,
        success,
      },
    });
  }

  /**
   * チャット送信イベントを記録する
   */
  public trackChatSend(streamId: string, messageType: string): void {
    this.logger.info('Chat message sent', {
      eventType: 'chat.message_send',
      eventData: {
        stream_id: streamId,
        message_type: messageType,
      },
    });
  }

  /**
   * 動画プレイヤー操作イベントを記録する
   */
  public trackPlayerAction(action: PlayerAction, streamId: string): void {
    this.logger.info('Player action', {
      eventType: 'player.action',
      eventData: {
        action,
        stream_id: streamId,
      },
    });
  }

  /**
   * タブ切り替えイベントを記録する
   */
  public trackTabChange(tabId: string, fromTab: string): void {
    this.logger.info('Tab changed', {
      eventType: 'ui.tab_change',
      eventData: {
        tab_id: tabId,
        from_tab: fromTab,
      },
    });
  }
}

// デフォルトエクスポート
export const analytics = AnalyticsLogger.getInstance();
